import numberToWords from "number-to-words";

const UNITS_CONFIG = {
  m: { singular: "meter", plural: "meters" },
  yd: { singular: "yard", plural: "yards" },
  yds: { singular: "yard", plural: "yards" },
  ft: { singular: "foot", plural: "feet" },
  in: { singular: "inch", plural: "inches" },
  mi: { singular: "mile", plural: "miles" },
  kg: { singular: "kilogram", plural: "kilograms" },
  s: { singular: "second", plural: "seconds" },
};

const MARKDOWN_REGEX = {
  codeBlocks: /(?:^ {0,3}```\w*|```|^ {0,3}~~~|~~~|`)/gm,
  headers: /^\s*#+ .*$/gm,
  inlineCode: /`([^`]+)`/g,
  links: /\[([^\]]+)\]\([^)]+\)/g,
  emphasis: /[*_]{1,2}([^*_]+)[*_]{1,2}/g,
  bullets: /^\s*[-*+]\s+/gm,
  emojis:
    /(?![0-9#*])(?:\p{Extended_Pictographic}(?:\u200d\p{Extended_Pictographic})*|\p{Emoji_Component})/gu,
  units: /(\b\d+)\s*(m|yd|yds|ft|in|mi|kg|s)\b/gi,
};

export const cleanText = text => {
  if (!text || typeof text !== "string") return "";

  let cleaned = text;

  // --- PHASE 1: MARKDOWN & STRUCTURE ---
  cleaned = cleaned
    .replace(MARKDOWN_REGEX.codeBlocks, "")
    .replace(MARKDOWN_REGEX.headers, "")
    .replace(MARKDOWN_REGEX.inlineCode, "$1")
    .replace(MARKDOWN_REGEX.links, "$1")
    .replace(MARKDOWN_REGEX.emphasis, "$1")
    .replace(MARKDOWN_REGEX.bullets, "");

  // --- PHASE 2: EMOJIS & HTML ---
  cleaned = cleaned.replace(MARKDOWN_REGEX.emojis, "");
  cleaned = cleaned.replace(/<[^>]+>/g, ""); // Remove HTML
  cleaned = cleaned.replace(/&[a-zA-Z0-9#]+;/g, ""); // Remove Entities

  // --- PHASE 3: REFINING FLOW ---
  // Convert ranges (e.g., "5-10") to "5 to 10" so the dash isn't spoken as "minus"
  cleaned = cleaned.replace(/(\d+)[–—-](\d+)/g, "$1 to $2");

  // --- PHASE 4: UNITS & PLURALIZATION ---
  // Finds numbers followed by units (e.g., "1m", "10yds")
  cleaned = cleaned.replace(MARKDOWN_REGEX.units, (match, numStr, unitKey) => {
    const num = parseInt(numStr, 10);
    const unitData = UNITS_CONFIG[unitKey.toLowerCase()];

    if (!unitData) return match;

    const unitLabel = num === 1 ? unitData.singular : unitData.plural;
    return `${numStr} ${unitLabel}`;
  });

  // --- PHASE 5: NUMBER TO WORDS ---
  cleaned = cleaned.replace(/\b\d+\b/g, match => {
    try {
      // number-to-words handles the conversion
      return numberToWords.toWords(match);
    } catch {
      return match;
    }
  });

  // --- PHASE 6: FINAL WHITESPACE VACUUM ---
  // 1. Pull punctuation back to words - From the emoji removal
  // 2. Collapse multiple spaces/tabs
  // 3. Keep paragraph breaks for natural pauses
  return cleaned
    .replace(/\s+([,.!?])/g, "$1")
    .replace(/[ \t]+/g, " ")
    .replace(/\n\s*\n/g, "\n\n")
    .trim();
};

// Helper function to convert base64 to Blob
const base64ToBlob = (base64, contentType) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
};

const createTTSAudioURL = async (text, hDVoice, onError = null) => {
  try {
    const apiKey = process.env.REACT_APP_GOOGLE_CLOUD_API_KEY;
    console.log(
      `hDVoice.languageCode: ${hDVoice.languageCode}, hDVoice.voiceName: ${hDVoice.voiceName}`,
    );
    if (!apiKey) {
      throw new Error("Google Cloud API key is missing. Please configure it.");
    }

    const response = await fetch(
      `https://texttospeech.googleapis.com/v1/text:synthesize?key=${apiKey}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          input: { text: text },
          voice: {
            languageCode: hDVoice.languageCode,
            // name: "es-US-Chirp3-HD-Autonoe",
            name: `${hDVoice.languageCode}-Chirp3-HD-${hDVoice.voiceName}`,
          },
          // audioConfig: {
          //   audioEncoding: "MP3",
          // },
          audioConfig: {
            audioEncoding: "LINEAR16",
            speakingRate: hDVoice.speakingRate,
          },
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Error details:", errorData);
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${
          errorData.error?.message || "Unknown error"
        }`,
      );
    }

    const data = await response.json();
    if (!data.audioContent) {
      throw new Error("No audio content returned from API");
    }

    const audioBlob = base64ToBlob(data.audioContent, "audio/mp3");
    return URL.createObjectURL(audioBlob);
  } catch (error) {
    console.error("Error synthesizing speech:", error);
    onError?.(error.message);
  }
};

export async function convertTextToSpeech(text, voiceConfig, onError = null) {
  // Clean the text before creating an audio URL
  const cleanedText = cleanText(text);

  if (!cleanedText) {
    onError?.("No valid text after cleaning");
    return;
  }
  // Return the audio URL
  return createTTSAudioURL(cleanedText, voiceConfig);
}
